/**
 * Where and when a mission happens: the five regions, the front line, and the
 * sun/wind/sky the briefing reads out.
 *
 * The five region names are the Mission Builder's own start-location list
 * (`RBMB.EXE`: London, Dunkirk, Somme, Paris, Verdun), and the Mission Builder
 * manual settles what they are — five fixed 80-mile-square maps, not raid
 * targets — with London confirmed as the England/home-defence region rather
 * than a trench sector (docs/game/aces-squadrons.md §5). Weather, wind and
 * time-of-day words are quoted verbatim from `PS.EXE`'s briefing templates
 * (docs/game/missions.md § *Mission setup parameters*).
 *
 * **Port-side simplification.** The real game draws a mission over a real
 * regional map with a real trench line on it. This port has one flying area
 * (`apps/web`'s `.WLD` region loader) and no trench geography, so the front is
 * modelled as a single plane in world space: a `z` coordinate, with one side of
 * it friendly. That is enough to give "cross the lines" and "get home" honest
 * meaning, and it is deliberately a *stated* stand-in rather than a claim about
 * the original's geometry.
 */

export const MISSION_REGIONS = ["london", "dunkirk", "somme", "paris", "verdun"] as const;

export type MissionRegion = (typeof MISSION_REGIONS)[number];

/** `RBMB.EXE`'s own capitalisation of the five start locations. */
export const REGION_LABELS: Readonly<Record<MissionRegion, string>> = {
  london: "London",
  dunkirk: "Dunkirk",
  somme: "Somme",
  paris: "Paris",
  verdun: "Verdun",
};

/** Verbatim `PS.EXE` sky words. */
export const SKY_CONDITIONS = ["clear", "partly cloudy", "cloudy", "overcast"] as const;
export type SkyCondition = (typeof SKY_CONDITIONS)[number];

/** Verbatim `PS.EXE` wind words. */
export const WIND_STRENGTHS = ["still", "light", "strong"] as const;
export type WindStrength = (typeof WIND_STRENGTHS)[number];

/** Verbatim `PS.EXE` time-of-day words. */
export const TIMES_OF_DAY = ["dawn", "daytime", "dusk", "night"] as const;
export type TimeOfDay = (typeof TIMES_OF_DAY)[number];

export interface MissionWeather {
  readonly sky: SkyCondition;
  readonly wind: WindStrength;
  readonly timeOfDay: TimeOfDay;
}

/**
 * The three career enlistment dates the game offers (docs/game/aircraft.md §10)
 * doubling as the single-mission era selector: which aeroplanes are in the sky
 * is a function of the date, and these are the three dates the game itself
 * uses.
 */
export const MISSION_ERAS = ["early", "mid", "late"] as const;

export type MissionEra = (typeof MISSION_ERAS)[number];

export const ERA_LABELS: Readonly<Record<MissionEra, string>> = {
  early: "Early war · 1915–16",
  mid: "Mid war · 1916–17",
  late: "Late war · 1918",
};

/**
 * The front, as a plane of constant `z`. Everything on the `friendlySign` side
 * of it is home; the other side is over the wire. Heading 0 in this engine flies
 * toward −z (`math3d.ts`: body forward is −Z), so a friendly side of `+1` puts
 * the player's aerodrome behind them and the lines ahead, which is the geometry
 * every briefing in the original assumes.
 */
export interface FrontLine {
  readonly zM: number;
  readonly friendlySign: 1 | -1;
}

export const DEFAULT_FRONT_LINE: FrontLine = { zM: 0, friendlySign: 1 };

/**
 * **Which regions actually have a front, and which one does not.**
 *
 * Four of the five are trench sectors and the front plane above is the port's
 * stand-in for their wire. **London is not.** It is the England/home-defence
 * region (docs/game/aces-squadrons.md §5), and there were no trenches in
 * Essex — confirmed twice over in the shipped data: `MAP1.DAT` is the one
 * briefing map of the five with no front polyline (world-terrain.md §2.5,
 * re-parsed for docs/engineering/london-sea-placements.md §1.3), and
 * `map/map-model.ts` already refuses to print a trench line across Kent.
 *
 * Until this existed, that fact had nowhere to live in the engine, and
 * `DEFAULT_FRONT_LINE`'s placeholder `zM = 0` was taken literally by
 * `flak-placement.ts`: London's batteries were laid out along a wire that does
 * not exist, i.e. in a tight belt around the player, wherever he happened to
 * be. `london-sea-placements.md` §3.2 measured the result — 434 of 434 sampled
 * belt positions in the North Sea, with a searchlight on every one of them, on
 * the one map where night interceptions and searchlights are the whole point.
 *
 * A region with no front still gets a `FrontLine` from `missionContext`,
 * because kill credit and the "Allied territory"/"German territory" phrase in
 * a briefing need *some* answer; what changes is that nothing lays guns along
 * it. See `MissionContext.hasFrontLine`.
 */
export const REGION_HAS_FRONT_LINE: Readonly<Record<MissionRegion, boolean>> = {
  london: false,
  dunkirk: true,
  somme: true,
  paris: true,
  verdun: true,
};

/** `true` for the four trench sectors, `false` for London — see `REGION_HAS_FRONT_LINE`. */
export function regionHasFrontLine(region: MissionRegion): boolean {
  return REGION_HAS_FRONT_LINE[region];
}

/** Signed distance into friendly territory; negative means over enemy lines. */
export function distanceInsideFriendlyM(front: FrontLine, zM: number): number {
  return (zM - front.zM) * front.friendlySign;
}

export function isFriendlyTerritory(front: FrontLine, zM: number): boolean {
  return distanceInsideFriendlyM(front, zM) >= 0;
}

/** The briefing's own phrase for where a flight starts, from `PS.EXE`'s `%g` slot. */
export function territoryLabel(front: FrontLine, zM: number): string {
  const inside = distanceInsideFriendlyM(front, zM);
  if (Math.abs(inside) < 500) return "the front";
  return inside > 0 ? "Allied territory" : "German territory";
}
